import { useEffect, useLayoutEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  MarketingHeaderContainer,
  MarketingNavigationItemsContainer,
  MarketingDialogsContainer,
} from "@platform/features/marketing";
import { AppLayout } from "./AppLayout";

export type Props = {
  children?: React.ReactNode;
  title: string;
};

export function MarketingLayout({ children, title }: Props) {
  const { hash, pathname } = useLocation();

  // An anchor in the address is scrolled to on the pages this layout wraps.
  // The homepage is left out: it scrolls to its own sections, with more care
  // about arriving against moving, and two scrolls for one address fight.
  useEffect(() => {
    if (hash && pathname !== "/") {
      const element = document.getElementById(hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash, pathname]);

  // Another page of the site starts at its top. The browser keeps the scroll
  // position across an in-app link, so a post opened from the News section
  // near the foot of the homepage opened at the foot of the post, which is
  // its own News section: it looked as though the link had done nothing.
  // An address with an anchor is scrolled to the anchor instead, above.
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
    // Only a new page, not a new anchor on the same one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // The header stays put over the page, so anything scrolled to must stop
  // below it rather than under it. Its height differs by screen size and is
  // read rather than assumed. It is set before the page's own effects run:
  // a page that scrolls to a section as it arrives does so in one of those,
  // and would otherwise land with the section's title under the header.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const header = document.querySelector("header");
    if (!header) return;
    const apply = () => {
      root.style.scrollPaddingTop = `${Math.round(header.getBoundingClientRect().height)}px`;
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      root.style.scrollPaddingTop = "";
    };
  }, []);

  return (
    <AppLayout
      header={
        <MarketingHeaderContainer>
          <MarketingNavigationItemsContainer />
        </MarketingHeaderContainer>
      }
      title={title}
    >
      {children ?? <Outlet />}
      <MarketingDialogsContainer />
    </AppLayout>
  );
}

export default MarketingLayout;
